import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Select,
  Table,
  Tab,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const ChannelPricingOverride = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [pricingList, setPricingList] = useState([]);
  const [groupRatioList, setGroupRatioList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [groupRatioModalOpen, setGroupRatioModalOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [editingGroupRatio, setEditingGroupRatio] = useState(null);
  const [formData, setFormData] = useState({
    channel_id: '',
    model_name: '',
    prompt_price: '',
    completion_price: '',
    model_ratio: '',
    completion_ratio: '',
    billing_type: 'per_token', // per_token=按量付费, per_call=按次付费
    per_call_price: '',
    per_call_price_rmb: '', // 按次付费人民币显示
  });
  const [groupRatioFormData, setGroupRatioFormData] = useState({
    channel_id: '',
    group_name: '',
    ratio: '',
  });

  // 获取所有渠道列表
  const fetchChannels = async () => {
    try {
      const res = await API.get('/api/channel/');
      const { success, data, message } = res.data;
      if (success) {
        // 过滤掉君云AIGC渠道（类型15），因为它们走独立的计费表
        const filteredChannels = data.filter(ch => ch.type !== 15);
        setChannels(filteredChannels.map(ch => ({
          key: ch.id,
          value: ch.id,
          text: `${ch.name} (ID: ${ch.id})`,
        })));
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to fetch channels');
    }
  };

  // 获取指定渠道的定价列表
  const fetchPricingList = async (channelId) => {
    if (!channelId) return;
    setLoading(true);
    try {
      const res = await API.get(`/api/channel-pricing-override/${channelId}`);
      const { success, data, message } = res.data;
      if (success) {
        setPricingList(data || []);
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to fetch pricing list');
    }
    setLoading(false);
  };

  // 获取指定渠道的分组倍率列表
  const fetchGroupRatioList = async (channelId) => {
    if (!channelId) return;
    setLoading(true);
    try {
      const res = await API.get(`/api/channel-pricing-override/${channelId}/group-ratios`);
      const { success, data, message } = res.data;
      if (success) {
        setGroupRatioList(data || []);
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to fetch group ratio list');
    }
    setLoading(false);
  };

  // 初始化时加载渠道列表
  useEffect(() => {
    fetchChannels();
  }, []);

  // 渠道改变时重新加载数据
  useEffect(() => {
    if (selectedChannelId) {
      fetchPricingList(selectedChannelId);
      fetchGroupRatioList(selectedChannelId);
    } else {
      setPricingList([]);
      setGroupRatioList([]);
    }
  }, [selectedChannelId]);

  // 打开定价新增/编辑弹窗
  const openModal = (pricing = null) => {
    if (pricing) {
      setEditingPricing(pricing);
      // 反算 ¥/1M tokens
      const promptPriceRmb = pricing.prompt_price > 0 
        ? (pricing.prompt_price * 1000000).toFixed(4) 
        : '';
      const completionPriceRmb = pricing.completion_price > 0 
        ? (pricing.completion_price * 1000000).toFixed(4) 
        : '';
      // 反算按次付费人民币
      const perCallPriceRmb = pricing.per_call_price > 0 
        ? pricing.per_call_price.toFixed(6) 
        : '';
      setFormData({
        channel_id: pricing.channel_id,
        model_name: pricing.model_name,
        prompt_price: pricing.prompt_price || '',
        completion_price: pricing.completion_price || '',
        prompt_price_rmb: promptPriceRmb,
        completion_price_rmb: completionPriceRmb,
        model_ratio: pricing.model_ratio || '',
        completion_ratio: pricing.completion_ratio || '',
        billing_type: pricing.billing_type || 'per_token',
        per_call_price: pricing.per_call_price || '',
        per_call_price_rmb: perCallPriceRmb,
      });
    } else {
      setEditingPricing(null);
      setFormData({
        channel_id: selectedChannelId,
        model_name: '',
        prompt_price: '',
        completion_price: '',
        prompt_price_rmb: '',
        completion_price_rmb: '',
        model_ratio: '',
        completion_ratio: '',
        billing_type: 'per_token',
        per_call_price: '',
        per_call_price_rmb: '',
      });
    }
    setModalOpen(true);
  };

  // 打开分组倍率新增/编辑弹窗
  const openGroupRatioModal = (ratio = null) => {
    if (ratio) {
      setEditingGroupRatio(ratio);
      setGroupRatioFormData({
        channel_id: ratio.channel_id,
        group_name: ratio.group_name,
        ratio: ratio.ratio || '',
      });
    } else {
      setEditingGroupRatio(null);
      setGroupRatioFormData({
        channel_id: selectedChannelId,
        group_name: '',
        ratio: '',
      });
    }
    setGroupRatioModalOpen(true);
  };

  // 关闭弹窗
  const closeModal = () => {
    setModalOpen(false);
    setEditingPricing(null);
    setFormData({
      channel_id: '',
      model_name: '',
      prompt_price: '',
      completion_price: '',
      model_ratio: '',
      completion_ratio: '',
      billing_type: 'per_token',
      per_call_price: '',
      per_call_price_rmb: '',
    });
  };

  // 关闭分组倍率弹窗
  const closeGroupRatioModal = () => {
    setGroupRatioModalOpen(false);
    setEditingGroupRatio(null);
    setGroupRatioFormData({
      channel_id: '',
      group_name: '',
      ratio: '',
    });
  };

  // 保存定价
  const savePricing = async () => {
    if (!formData.model_name.trim()) {
      showError('Model name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        channel_id: parseInt(selectedChannelId),
        model_name: formData.model_name.trim(),
        prompt_price: parseFloat(formData.prompt_price) || 0,
        completion_price: parseFloat(formData.completion_price) || 0,
        model_ratio: parseFloat(formData.model_ratio) || 0,
        completion_ratio: parseFloat(formData.completion_ratio) || 0,
        billing_type: formData.billing_type || 'per_token',
        per_call_price: parseFloat(formData.per_call_price) || 0,
        is_enabled: 1,
      };

      const res = await API.post('/api/channel-pricing-override/', payload);
      const { success, message } = res.data;
      if (success) {
        showSuccess(editingPricing ? 'Pricing updated successfully' : 'Pricing created successfully');
        closeModal();
        fetchPricingList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to save pricing');
    }
    setLoading(false);
  };

  // 保存分组倍率
  const saveGroupRatio = async () => {
    if (!groupRatioFormData.group_name.trim()) {
      showError('Group name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        channel_id: parseInt(selectedChannelId),
        group_name: groupRatioFormData.group_name.trim(),
        ratio: parseFloat(groupRatioFormData.ratio) || 1,
        is_enabled: 1,
      };

      const res = await API.post('/api/channel-pricing-override/group-ratio', payload);
      const { success, message } = res.data;
      if (success) {
        showSuccess(editingGroupRatio ? 'Group ratio updated successfully' : 'Group ratio created successfully');
        closeGroupRatioModal();
        fetchGroupRatioList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to save group ratio');
    }
    setLoading(false);
  };

  // 删除定价
  const deletePricing = async (pricing) => {
    if (!window.confirm(`Are you sure you want to delete pricing for "${pricing.model_name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const res = await API.delete(`/api/channel-pricing-override/${pricing.channel_id}/${pricing.model_name}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('Pricing deleted successfully');
        fetchPricingList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to delete pricing');
    }
    setLoading(false);
  };

  // 删除分组倍率
  const deleteGroupRatio = async (ratio) => {
    if (!window.confirm(`Are you sure you want to delete group ratio for "${ratio.group_name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const res = await API.delete(`/api/channel-pricing-override/${ratio.channel_id}/group-ratio/${ratio.group_name}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('Group ratio deleted successfully');
        fetchGroupRatioList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to delete group ratio');
    }
    setLoading(false);
  };

  // 重新加载配置到内存
  const reloadPricing = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/channel-pricing-override/reload');
      const { success, message } = res.data;
      if (success) {
        showSuccess('Configuration reloaded successfully');
      } else {
        showError(message);
      }
    } catch (err) {
      showError('Failed to reload configuration');
    }
    setLoading(false);
  };

  // 模型定价表格
  const PricingTable = () => (
    <>
      <Button
        primary
        icon
        labelPosition='left'
        onClick={() => openModal()}
        disabled={!selectedChannelId}
        loading={loading}
        style={{ marginBottom: '1em' }}
      >
        <Icon name='plus' />
        新增模型定价
      </Button>

      {selectedChannelId && pricingList.length === 0 && !loading && (
        <Message warning>
          <Message.Header>暂无配置</Message.Header>
          <p>该渠道下暂无模型定价配置，点击"新增模型定价"按钮添加。</p>
        </Message>
      )}

      {selectedChannelId && pricingList.length > 0 && (
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>模型名称</Table.HeaderCell>
              <Table.HeaderCell>计费类型</Table.HeaderCell>
              <Table.HeaderCell>输入价格</Table.HeaderCell>
              <Table.HeaderCell>输出价格</Table.HeaderCell>
              <Table.HeaderCell>每次价格</Table.HeaderCell>
              <Table.HeaderCell>模型倍率</Table.HeaderCell>
              <Table.HeaderCell>补全倍率</Table.HeaderCell>
              <Table.HeaderCell textAlign='center'>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pricingList.map((pricing) => (
              <Table.Row key={pricing.id}>
                <Table.Cell>
                  <Label>{pricing.model_name}</Label>
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_call' 
                    ? <Label color='orange'>按次付费</Label>
                    : <Label color='blue'>按量付费</Label>}
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_token' && pricing.prompt_price > 0 
                    ? `¥${(pricing.prompt_price * 1000000).toFixed(4)} / 1M`
                    : '-'}
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_token' && pricing.completion_price > 0 
                    ? `¥${(pricing.completion_price * 1000000).toFixed(4)} / 1M`
                    : '-'}
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_call' && pricing.per_call_price > 0 
                    ? `¥${pricing.per_call_price.toFixed(6)} / 次`
                    : '-'}
                </Table.Cell>
                <Table.Cell>{pricing.model_ratio > 0 ? pricing.model_ratio : '-'}</Table.Cell>
                <Table.Cell>{pricing.completion_ratio > 0 ? pricing.completion_ratio : '-'}</Table.Cell>
                <Table.Cell textAlign='center'>
                  <Button
                    icon
                    size='mini'
                    onClick={() => openModal(pricing)}
                    title='编辑'
                  >
                    <Icon name='edit' />
                  </Button>
                  <Button
                    icon
                    size='mini'
                    color='red'
                    onClick={() => deletePricing(pricing)}
                    title='删除'
                  >
                    <Icon name='trash' />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );

  // 分组倍率表格
  const GroupRatioTable = () => (
    <>
      <Button
        primary
        icon
        labelPosition='left'
        onClick={() => openGroupRatioModal()}
        disabled={!selectedChannelId}
        loading={loading}
        style={{ marginBottom: '1em' }}
      >
        <Icon name='plus' />
        新增分组倍率
      </Button>

      {selectedChannelId && groupRatioList.length === 0 && !loading && (
        <Message info>
          <Message.Header>使用全局分组倍率</Message.Header>
          <p>该渠道下暂无分组倍率配置，将使用全局分组倍率设置。</p>
          <p>点击"新增分组倍率"按钮添加渠道专属分组倍率。</p>
        </Message>
      )}

      {selectedChannelId && groupRatioList.length > 0 && (
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>分组名称</Table.HeaderCell>
              <Table.HeaderCell>倍率</Table.HeaderCell>
              <Table.HeaderCell textAlign='center'>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {groupRatioList.map((ratio) => (
              <Table.Row key={ratio.id}>
                <Table.Cell>
                  <Label>{ratio.group_name}</Label>
                </Table.Cell>
                <Table.Cell>{ratio.ratio}</Table.Cell>
                <Table.Cell textAlign='center'>
                  <Button
                    icon
                    size='mini'
                    onClick={() => openGroupRatioModal(ratio)}
                    title='编辑'
                  >
                    <Icon name='edit' />
                  </Button>
                  <Button
                    icon
                    size='mini'
                    color='red'
                    onClick={() => deleteGroupRatio(ratio)}
                    title='删除'
                  >
                    <Icon name='trash' />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );

  const panes = [
    {
      menuItem: '模型定价',
      render: () => (
        <Tab.Pane attached={false}>
          <PricingTable />
        </Tab.Pane>
      ),
    },
    {
      menuItem: '分组倍率',
      render: () => (
        <Tab.Pane attached={false}>
          <GroupRatioTable />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <Header as='h3'>
        <Icon name='dollar sign' />
        <Header.Content>
          渠道定价覆盖管理
          <Header.Subheader>按渠道隔离管理模型计费配置和分组倍率</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Grid columns={2} stackable>
          <Grid.Column width={10}>
            <Form>
              <Form.Field>
                <label>选择渠道</label>
                <Select
                  placeholder='请选择渠道'
                  options={channels}
                  value={selectedChannelId}
                  onChange={(e, { value }) => setSelectedChannelId(value)}
                  search
                  selection
                />
              </Form.Field>
            </Form>
          </Grid.Column>
          <Grid.Column width={6} textAlign='right' verticalAlign='bottom'>
            <Button
              icon
              labelPosition='left'
              onClick={reloadPricing}
              loading={loading}
            >
              <Icon name='refresh' />
              重载配置
            </Button>
          </Grid.Column>
        </Grid>

        {!selectedChannelId && (
          <Message info style={{ marginTop: '1em' }}>
            <Message.Header>提示</Message.Header>
            <p>请先选择渠道，然后管理该渠道下的模型定价配置和分组倍率。</p>
            <p><strong>注意：</strong>君云AIGC渠道（类型15）的计费仍走独立的 channel_model_pricing 表，不受此处配置影响。</p>
          </Message>
        )}

        {selectedChannelId && (
          <Tab
            menu={{ secondary: true, pointing: true }}
            panes={panes}
            style={{ marginTop: '1em' }}
          />
        )}
      </Segment>

      {/* 定价弹窗 */}
      <Modal open={modalOpen} onClose={closeModal} size='small'>
        <Modal.Header>
          {editingPricing ? '编辑定价配置' : '新增定价配置'}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>渠道ID</label>
              <Input value={selectedChannelId} disabled />
            </Form.Field>
            <Form.Field required>
              <label>模型名称</label>
              <Input
                placeholder='例如: gemini-3-pro-preview'
                value={formData.model_name}
                onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                disabled={!!editingPricing}
              />
            </Form.Field>
            <Form.Field required>
              <label>计费类型</label>
              <Select
                options={[
                  { key: 'per_token', text: '按量付费（按 token 数量）', value: 'per_token' },
                  { key: 'per_call', text: '按次付费（按调用次数）', value: 'per_call' },
                ]}
                value={formData.billing_type}
                onChange={(e, { value }) => setFormData({ ...formData, billing_type: value })}
              />
            </Form.Field>

            {/* 按量付费字段 */}
            {formData.billing_type === 'per_token' && (
              <>
                <Form.Field>
                  <label>输入价格 (¥/1M tokens)</label>
                  <Input
                    type='number'
                    step='0.0001'
                    placeholder='例如: 21.9'
                    value={formData.prompt_price_rmb}
                    onChange={(e) => {
                      const rmbPer1M = parseFloat(e.target.value) || 0;
                      // 换算: ¥/1M tokens -> quota/token
                      // ¥1 = 1 quota
                      // ¥/1M tokens / 1,000,000 = quota/token
                      const quotaPerToken = (rmbPer1M / 1000000).toFixed(6);
                      setFormData({ 
                        ...formData, 
                        prompt_price_rmb: e.target.value,
                        prompt_price: quotaPerToken
                      });
                    }}
                  />
                  <small>系统会自动换算为 quota/token: {formData.prompt_price || '0'}</small>
                </Form.Field>
                <Form.Field>
                  <label>输出价格 (¥/1M tokens)</label>
                  <Input
                    type='number'
                    step='0.0001'
                    placeholder='例如: 131.4'
                    value={formData.completion_price_rmb}
                    onChange={(e) => {
                      const rmbPer1M = parseFloat(e.target.value) || 0;
                      const quotaPerToken = (rmbPer1M / 1000000).toFixed(6);
                      setFormData({ 
                        ...formData, 
                        completion_price_rmb: e.target.value,
                        completion_price: quotaPerToken
                      });
                    }}
                  />
                  <small>系统会自动换算为 quota/token: {formData.completion_price || '0'}</small>
                </Form.Field>
              </>
            )}

            {/* 按次付费字段 */}
            {formData.billing_type === 'per_call' && (
              <Form.Field>
                <label>每次价格 (¥/次)</label>
                <Input
                  type='number'
                  step='0.000001'
                  placeholder='例如: 0.10'
                  value={formData.per_call_price_rmb}
                  onChange={(e) => {
                    const rmbPerCall = parseFloat(e.target.value) || 0;
                    // ¥1 = 1 quota
                    setFormData({ 
                      ...formData, 
                      per_call_price_rmb: e.target.value,
                      per_call_price: rmbPerCall.toFixed(6)
                    });
                  }}
                />
                <small>系统会自动换算为 quota/次: {formData.per_call_price || '0'}</small>
              </Form.Field>
            )}

            <Form.Field>
              <label>模型倍率 (兼容旧逻辑)</label>
              <Input
                type='number'
                step='0.0001'
                placeholder='例如: 1'
                value={formData.model_ratio}
                onChange={(e) => setFormData({ ...formData, model_ratio: e.target.value })}
              />
              <small>留空或填0表示使用全局配置</small>
            </Form.Field>
            <Form.Field>
              <label>补全倍率</label>
              <Input
                type='number'
                step='0.0001'
                placeholder='例如: 6'
                value={formData.completion_ratio}
                onChange={(e) => setFormData({ ...formData, completion_ratio: e.target.value })}
              />
              <small>留空或填0表示使用全局配置</small>
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeModal}>取消</Button>
          <Button primary onClick={savePricing} loading={loading}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 分组倍率弹窗 */}
      <Modal open={groupRatioModalOpen} onClose={closeGroupRatioModal} size='small'>
        <Modal.Header>
          {editingGroupRatio ? '编辑分组倍率' : '新增分组倍率'}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>渠道ID</label>
              <Input value={selectedChannelId} disabled />
            </Form.Field>
            <Form.Field required>
              <label>分组名称</label>
              <Input
                placeholder='例如: default, vip, svip'
                value={groupRatioFormData.group_name}
                onChange={(e) => setGroupRatioFormData({ ...groupRatioFormData, group_name: e.target.value })}
                disabled={!!editingGroupRatio}
              />
            </Form.Field>
            <Form.Field required>
              <label>倍率</label>
              <Input
                type='number'
                step='0.0001'
                placeholder='例如: 1.5'
                value={groupRatioFormData.ratio}
                onChange={(e) => setGroupRatioFormData({ ...groupRatioFormData, ratio: e.target.value })}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeGroupRatioModal}>取消</Button>
          <Button primary onClick={saveGroupRatio} loading={loading}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ChannelPricingOverride;
